/**
 * Wire-protocol mirrors. Keep these in sync with server/src/schemas.ts.
 *
 * NB: we never construct these in the app; we only consume what the
 * Worker returns. So we keep them as plain `type` aliases with no
 * runtime guard — the server already validated.
 */

export type StrategyKey =
  | "swing"
  | "trailing"
  | "copy"
  | "wheel"
  | "intraday"
  | "crypto";

export const STRATEGIES: readonly StrategyKey[] = [
  "swing",
  "trailing",
  "copy",
  "wheel",
  "intraday",
  "crypto",
] as const;

// Short labels for the overview cards — fits in 12 chars.
export const STRATEGY_LABELS: Record<StrategyKey, string> = {
  swing: "swing",
  trailing: "trailing",
  copy: "copy",
  wheel: "wheel",
  intraday: "intraday (live MCP)",
  crypto: "crypto (24/7)",
};

export type HeartbeatStatus =
  | "ok"
  | "error"
  | "paused"
  | "market_closed"
  | "skipped"
  | "deferred";

export type Heartbeat = {
  strategy: StrategyKey;
  last_run_at_utc: string;
  status: HeartbeatStatus;
  source_routine: string;
  next_expected_at_utc: string | null;
  note: string;
};

export type NavLatest = {
  trading_day_et: string;
  strategy: StrategyKey;
  nav_cents: number;
  spy_close_cents: number;
  recorded_at_utc: string;
  source_routine: string;
};

export type DecisionsToday = { allowed: number; denied: number };

export type Exposure = {
  ceiling_pct: number;
  regime: string;
  as_of_et: string;
  rationale: string;
};

export type LiveConfig = {
  live_allowed: boolean;
  kill_switch_active: boolean;
  kill_switch_reason: string | null;
  live_max_notional_cents: number | null;
  strategies_live_approved: StrategyKey[];
  pending_trades_count: number;
  require_approval: boolean;
  as_of_utc: string;
};

export type StatusResponse = {
  generated_at_utc: string;
  exposure: Exposure | null;
  live_config: LiveConfig | null;
  strategies: Record<
    StrategyKey,
    {
      heartbeat: Heartbeat | null;
      nav_latest: NavLatest | null;
      decisions_today: DecisionsToday;
    }
  >;
};

export type OpenPosition = {
  symbol: string;
  qty: number;
  avg_entry_price_cents: number;
  current_price_cents: number;
  market_value_cents: number;
  unrealized_pl_cents: number;
  unrealized_plpc: number;
  side: string;
};

export type NavRow = {
  trading_day_et: string;
  nav_cents: number;
  spy_close_cents: number;
  // Detail-view: per-position snapshot at row time. Null on rows from
  // before the dashboard schema upgrade or when the bot didn't send.
  open_positions: OpenPosition[] | null;
};

export type ExitType =
  | "stop"
  | "take_profit"
  | "session_end"
  | "time_limit"
  | "manual"
  | "signal_invalidated";

export type DecisionRow = {
  id: number;
  symbol: string;
  side: "buy" | "sell";
  qty: number;
  // Fractional precision for crypto + partial-share trades. Null for
  // rows from before the 2026-05-18 schema upgrade. When present, the
  // UI should prefer this over `qty` (which is floor'd to int).
  qty_decimal: number | null;
  limit_price_cents: number | null;
  allowed: number; // 0 | 1
  reasons_json: string;
  nav_at_check_cents: number;
  cash_at_check_cents: number;
  exposure_ceiling: number;
  timestamp_utc: string;
  // Detail-view extras (nullable for pre-upgrade rows):
  setup_name: string | null;
  open_positions_count: number | null;
  thesis_id: string | null;
  // Live-safety: was this real money?
  is_live: number | null;  // 0 | 1 | null (null on rows pre-upgrade)
  broker_name: "alpaca" | "ibkr" | null;
};

export type ThesisRow = {
  thesis_id: string;
  ticker: string;
  name: string;
  state: "IDEA" | "ENTRY_READY" | "ACTIVE" | "CLOSED" | "INVALIDATED";
  timestamp_utc: string;
  trading_day_et: string;
  note: string | null;
  catalyst: string | null;
  invalidation: string | null;
  fill_price_cents: number | null;
  exit_price_cents: number | null;
  closed_at_pnl_pct: number | null;
  // Detail-view extra (nullable when state != CLOSED or pre-upgrade):
  exit_type: ExitType | null;
  // Live-safety: was the trade real money?
  is_live: number | null;
};

// Intraday scan — what the bot saw on its universe at one moment.
// Mirrors `IntradayScanBody` + per-symbol breakdown.
export type IntradayScanSymbol = {
  symbol: string;
  bars_count: number;
  last_bar_time: string | null;
  last_close_cents: number | null;
  last_volume: number | null;
  avg_volume_20: number | null;
  setup_name: string | null;
  setup_entry_cents: number | null;
  setup_evidence: string | null;
  is_pick: boolean;
  error: string | null;
};

export type IntradayScan = {
  scan_at_utc: string;
  pick_symbol: string | null;
  symbols: IntradayScanSymbol[];
  notes: string[];
  received_at_utc: string;
};

// Head-trader agent journal entry (per fire of the meta-trader-agent
// routine). Surfaced on the Overview so the operator sees what the
// bot is "thinking" right now.
export type TraderJournalEntry = {
  entry_at_utc: string;
  regime: string | null;
  headline: string;
  body_md: string;
  flags: string[];
  received_at_utc: string;
};

// DuckDB analytical readouts ("Intel & Ops" panel). loop-driver's
// tailer-populated DuckDB store, condensed to a headline snapshot and
// pushed periodically. All sections optional — the bot may add/drop
// readouts; the panel renders what's present.
export type DuckdbMetrics = {
  signal_pulse?: {
    total_events: number;
    symbols: number;
    last24h_total: number;
    by_kind: { kind: string; n: number; last24h: number }[];
  };
  decisions?: {
    total: number;
    allowed: number;
    denied: number;
    allow_pct: number;
    by_strategy: { strategy: string; n: number; allow_pct: number }[];
    top_denials: { reason: string; n: number }[];
  };
  fleet?: {
    spawns_ok: number;
    kills_ok: number;
    auth_401_blocks: number;
    by_role: { role: string; n: number }[];
  };
  positions?: {
    total: number;
    symbols: number;
    by_strategy: { strategy: string; n: number; avg_qty: number }[];
  };
  db_totals?: Record<string, number>;
};

export type DuckdbMetricsResponse = {
  computed_at_utc: string | null;
  metrics: DuckdbMetrics | null;
  received_at_utc: string | null;
};

export type PostmortemRow = {
  thesis_id: string;
  ticker: string;
  outcome: "WIN" | "LOSS" | "SCRATCH";
  signal:
    | "TRUE_POSITIVE"
    | "FALSE_POSITIVE"
    | "MISSED_OPPORTUNITY"
    | "NOISE";
  pnl_pct: number;
  pnl_cents: number;
  reason: string;
  lesson: string | null;
  timestamp_utc: string;
  trading_day_et: string;
};
